import React, { Component } from "react";
import { Link, NavLink } from "react-router-dom";
import "./Style.css";

type Props = {
  title: string;
};

type State = {
  isCompact: boolean;
  isHidden: boolean;
  isStatsOpen: boolean;
};

class Header extends Component<Props, State> {
  private headerRef = React.createRef<HTMLElement>();
  private lastScrollY = 0;

  state: State = {
    isCompact: false,
    isHidden: false,
    isStatsOpen: false,
  };

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("mousedown", this.handleOutsideClick);
    window.addEventListener("touchstart", this.handleOutsideClick, { passive: true });
    window.addEventListener("keydown", this.handleKeyDown);
    this.handleScroll();
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("mousedown", this.handleOutsideClick);
    window.removeEventListener("touchstart", this.handleOutsideClick);
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  handleScroll = () => {
    const scrollY = Math.max(window.scrollY || window.pageYOffset, 0);
    const compactThreshold = 48;
    const expandThreshold = 12;
    const hideThreshold = 56;
    const scrollDelta = scrollY - this.lastScrollY;
    const isScrollingDown = scrollDelta > 0;
    const isScrollingUp = scrollDelta < 0;

    const isCompact = scrollY > compactThreshold
      ? true
      : scrollY < expandThreshold
        ? false
        : this.state.isCompact;

    const isHidden =
      !this.state.isStatsOpen &&
      scrollY > hideThreshold &&
      (isScrollingDown || (!isScrollingUp && this.state.isHidden));

    this.lastScrollY = scrollY;

    if (isCompact !== this.state.isCompact || isHidden !== this.state.isHidden) {
      this.setState({ isCompact, isHidden });
    }
  };

  handleOutsideClick = (event: MouseEvent | TouchEvent) => {
    if (!this.state.isStatsOpen) {
      return;
    }

    const targetNode = event.target as Node | null;
    if (targetNode && this.headerRef.current?.contains(targetNode)) {
      return;
    }

    this.setState({ isStatsOpen: false });
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.state.isStatsOpen) {
      this.setState({ isStatsOpen: false });
    }
  };

  openStatsMenu = () => {
    if (!this.state.isStatsOpen) {
      this.setState({ isHidden: false, isStatsOpen: true });
    }
  };

  closeStatsMenu = () => {
    if (this.state.isStatsOpen) {
      this.setState({ isStatsOpen: false });
    }
  };

  render() {
    const { title } = this.props;
    const headerClass = [
      "header",
      this.state.isCompact ? "compact" : "",
      this.state.isHidden ? "is-hidden" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const primaryMenuItems = [
      { to: "/", label: "Home" },
      { to: "/convert", label: "Converter" },
      { to: "/glossary", label: "Glossary" },
    ];
    const statsMenuItems = [
      { to: "/hashrate", label: "Hashrate" },
      { to: "/emission", label: "Emission" },
      { to: "/inflation", label: "Inflation" },
      { to: "/halving", label: "Halvings" },
      { to: "/fear-greed", label: "Fear & Greed" },
    ];
    const activePath = window.location.pathname;
    const isStatsRouteActive = statsMenuItems.some((item) => item.to === activePath);
    const statsButtonClassName = [
      "menu-link",
      "menu-button",
      isStatsRouteActive || this.state.isStatsOpen ? "is-active" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <header className={headerClass} ref={this.headerRef}>
        <div className="content">
          <div className="top-row">
            <Link to="/" className="title title-link">
              {title}
            </Link>
            <img src={"/images/bitcoin.png"} className="logo" alt="Bitcoin logo" />
          </div>
          <nav className="header-menu" aria-label="Primary">
            <NavLink
              to={primaryMenuItems[0].to}
              className={({ isActive }) =>
                isActive ? "menu-link is-active" : "menu-link"
              }
              onClick={this.closeStatsMenu}
            >
              {primaryMenuItems[0].label}
            </NavLink>
            {this.renderMenuSeparator()}
            <div
              className={
                this.state.isStatsOpen
                  ? "menu-dropdown-wrapper is-open"
                  : "menu-dropdown-wrapper"
              }
              onMouseEnter={this.openStatsMenu}
              onMouseLeave={this.closeStatsMenu}
            >
              <button
                type="button"
                className={statsButtonClassName}
                aria-expanded={this.state.isStatsOpen}
                aria-controls="stats-menu-panel"
                onClick={this.openStatsMenu}
              >
                Metrics
              </button>
              <div id="stats-menu-panel" className="stats-dropdown" role="menu" aria-label="Stats pages">
                {statsMenuItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    className={({ isActive }) =>
                      isActive ? "stats-dropdown-link is-active" : "stats-dropdown-link"
                    }
                    onClick={this.closeStatsMenu}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
            {this.renderMenuSeparator()}
            {primaryMenuItems.slice(1).map((item) => (
              <React.Fragment key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    isActive ? "menu-link is-active" : "menu-link"
                  }
                  onClick={this.closeStatsMenu}
                >
                  {item.label}
                </NavLink>
                {item.to !== primaryMenuItems[primaryMenuItems.length - 1].to &&
                  this.renderMenuSeparator()}
              </React.Fragment>
            ))}
          </nav>
        </div>
      </header>
    );
  }

  renderMenuSeparator = () => (
    <span className="menu-separator" aria-hidden="true">
      |
    </span>
  );
}

export default Header;
